import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import apiService from "../../services/api";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import BeatLoader from "react-spinners/BeatLoader";
import "./certificates.scss";

const Certificates = () => {
  const { currentUser } = useContext(AuthContext);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 15;

  // Issue modal state
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueError, setIssueError] = useState("");
  const [issueSuccess, setIssueSuccess] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [issueForm, setIssueForm] = useState({
    share_class: "Ordinary",
    number_of_shares: "",
    amount_paid: "",
    notes: "",
  });

  // Revoke modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [revokeLoading, setRevokeLoading] = useState(false);

  // Bulk issuance state
  const [bulkLoading, setBulkLoading] = useState(false);

  const isAdmin =
    currentUser?.staffData?.role === "super-user" ||
    currentUser?.staffData?.role === "admin";

  // Fetch certificates
  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.getCertificates({
        search,
        status: statusFilter,
        limit,
        offset: page * limit,
      });
      setCertificates(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error fetching certificates:", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  // Search for users to issue certificate to
  const searchUsers = useCallback(async (query) => {
    if (query.length < 2) {
      setUserResults([]);
      setUserSearching(false);
      return;
    }
    setUserSearching(true);
    try {
      const result = await apiService.getUsers({
        search: query,
        limit: 10,
      });
      setUserResults(result.data || []);
    } catch (err) {
      console.error("Error searching users:", err);
    } finally {
      setUserSearching(false);
    }
  }, []);

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearch) searchUsers(userSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, searchUsers]);

  // Handle issue certificate
  const handleIssueCertificate = async (e) => {
    e.preventDefault();
    setIssueError("");
    setIssueSuccess("");

    if (!selectedUser) {
      setIssueError("Please select a shareholder");
      return;
    }

    if (
      !issueForm.number_of_shares ||
      parseFloat(issueForm.number_of_shares) <= 0
    ) {
      setIssueError("Number of shares must be a positive number");
      return;
    }

    if (!issueForm.amount_paid || parseFloat(issueForm.amount_paid) <= 0) {
      setIssueError("Amount paid must be a positive number");
      return;
    }

    setIssueLoading(true);
    try {
      const result = await apiService.issueCertificate({
        user_id: selectedUser.id,
        share_class: issueForm.share_class,
        number_of_shares: parseFloat(issueForm.number_of_shares),
        amount_paid: parseFloat(issueForm.amount_paid),
        notes: issueForm.notes,
      });

      if (result.success) {
        setIssueSuccess(result.message || "Certificate issued successfully!");
        setTimeout(() => {
          setShowIssueModal(false);
          resetIssueForm();
          fetchCertificates();
        }, 1500);
      } else {
        setIssueError(result.error || "Failed to issue certificate");
      }
    } catch (err) {
      setIssueError("An unexpected error occurred");
    } finally {
      setIssueLoading(false);
    }
  };

  // Handle bulk certificate issuance
  const handleBulkIssue = async () => {
    const ok = window.confirm(
      "Issue a share certificate to every shareholder who holds shares but does not already have one?\n\n" +
        "Each certificate uses the shareholder's current holding. Shareholders who already have an active certificate are skipped. This may take up to a minute."
    );
    if (!ok) return;

    setBulkLoading(true);
    try {
      const result = await apiService.bulkIssueCertificates();
      if (result.success) {
        alert(
          `${result.message}\n\nIssued: ${result.issued_count}\nAlready had one: ${result.already_active}\nEligible total: ${result.eligible_total}`
        );
        setPage(0);
        fetchCertificates();
      } else {
        alert(result.error || "Bulk issuance failed");
      }
    } catch (err) {
      alert("An unexpected error occurred during bulk issuance");
    } finally {
      setBulkLoading(false);
    }
  };

  // Handle revoke certificate
  const handleRevoke = async () => {
    if (!revokeReason.trim()) return;
    setRevokeLoading(true);

    try {
      const result = await apiService.revokeCertificate(
        revokeTarget.id,
        revokeReason.trim(),
      );

      if (result.success) {
        setShowRevokeModal(false);
        setRevokeTarget(null);
        setRevokeReason("");
        fetchCertificates();
      } else {
        alert(result.error || "Failed to revoke certificate");
      }
    } catch (err) {
      alert("An unexpected error occurred");
    } finally {
      setRevokeLoading(false);
    }
  };

  const resetIssueForm = () => {
    setSelectedUser(null);
    setUserSearch("");
    setUserResults([]);
    setIssueForm({
      share_class: "Ordinary",
      number_of_shares: "",
      amount_paid: "",
      notes: "",
    });
    setIssueError("");
    setIssueSuccess("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="certificates">
      <Sidebar />
      <div className="certificatesContainer">
        <Navbar />
        <div className="certificatesContent">
          {/* Header */}
          <div className="pageHeader">
            <div className="headerLeft">
              <h1>Share Certificates</h1>
              <span className="totalCount">{total} certificates</span>
            </div>
            {isAdmin && (
              <div className="headerActions">
                <button
                  className="bulkBtn"
                  onClick={handleBulkIssue}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? "Issuing…" : "Bulk Issue Certificates"}
                </button>
                <button
                  className="issueBtn"
                  onClick={() => {
                    resetIssueForm();
                    setShowIssueModal(true);
                  }}
                >
                  + Issue New Certificate
                </button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="filtersBar">
            <input
              type="text"
              placeholder="Search by name or certificate number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="searchInput"
              id="cert-search"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className="statusSelect"
              id="cert-status-filter"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="tableLoader">
              <BeatLoader color="#7451f8" />
            </div>
          ) : certificates.length === 0 ? (
            <div className="emptyState">
              <p>No certificates found</p>
            </div>
          ) : (
            <>
              <div className="tableWrapper">
                <table className="certTable">
                  <thead>
                    <tr>
                      <th>Certificate No.</th>
                      <th>Shareholder</th>
                      <th>Class</th>
                      <th>Shares</th>
                      <th>Amount Paid</th>
                      <th>Date Issued</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map((cert) => (
                      <tr
                        key={cert.id}
                        className={cert.status === "revoked" ? "revoked" : ""}
                      >
                        <td className="certNum">
                          <strong>{cert.certificate_number}</strong>
                        </td>
                        <td>
                          <div className="holderInfo">
                            <span className="name">
                              {cert.shareholder_name}
                            </span>
                            {cert.membership_code && (
                              <span className="memberCode">
                                {cert.membership_code}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{cert.share_class}</td>
                        <td className="numericCell">{cert.number_of_shares}</td>
                        <td className="numericCell">
                          GH₵{" "}
                          {parseFloat(cert.amount_paid).toLocaleString(
                            "en-GH",
                            { minimumFractionDigits: 2 },
                          )}
                        </td>
                        <td>{formatDate(cert.issued_date)}</td>
                        <td>
                          <span className={`statusBadge ${cert.status}`}>
                            {cert.status}
                          </span>
                        </td>
                        <td className="actionsCell">
                          <Link
                            to={`/certificates/${cert.id}`}
                            className="viewBtn"
                            title="View / Print"
                          >
                            👁️
                          </Link>
                          {isAdmin && cert.status === "active" && (
                            <button
                              className="revokeBtn"
                              title="Revoke"
                              onClick={() => {
                                setRevokeTarget(cert);
                                setRevokeReason("");
                                setShowRevokeModal(true);
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                    className="pageBtn"
                  >
                    ← Previous
                  </button>
                  <span className="pageInfo">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    className="pageBtn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Issue Certificate Modal */}
        {showIssueModal && (
          <div
            className="modalOverlay"
            onClick={() => setShowIssueModal(false)}
          >
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h2>Issue Share Certificate</h2>
                <button
                  className="closeBtn"
                  onClick={() => setShowIssueModal(false)}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleIssueCertificate} className="issueForm">
                {/* User search/select */}
                <div className="formGroup">
                  <label>Shareholder *</label>
                  {selectedUser ? (
                    <div className="selectedUser">
                      <div className="userInfo">
                        <strong>{selectedUser.display_name}</strong>
                        <span>
                          {selectedUser.membership_code} — {selectedUser.email}
                        </span>
                        {selectedUser.address && (
                          <span className="userAddress">
                            📍 {selectedUser.address}
                          </span>
                        )}
                        <span className="userShares">
                          Shares: {selectedUser.share_amount || 0} | Value: GH₵{" "}
                          {selectedUser.share_value ||
                            selectedUser.share_amount ||
                            0}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="changeBtn"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearch("");
                          setUserResults([]);
                          setIssueForm((prev) => ({
                            ...prev,
                            number_of_shares: "",
                            amount_paid: "",
                          }));
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="searchWrapper">
                      <input
                        type="text"
                        placeholder="Type at least 2 characters to search..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="formInput"
                        id="user-search-input"
                        autoFocus
                      />
                      {userSearching && (
                        <div className="searchFeedback">Searching...</div>
                      )}
                      {!userSearching &&
                        userSearch.length >= 2 &&
                        userResults.length === 0 && (
                          <div className="searchFeedback noResults">
                            No shareholders found for "{userSearch}"
                          </div>
                        )}
                      {userResults.length > 0 && (
                        <div className="userDropdown">
                          {userResults.map((user) => (
                            <div
                              key={user.id}
                              className="userOption"
                              onClick={() => {
                                setSelectedUser(user);
                                setUserResults([]);
                                setUserSearch("");
                                setIssueForm((prev) => ({
                                  ...prev,
                                  number_of_shares: user.share_amount || "",
                                  amount_paid:
                                    user.share_value || user.share_amount || "",
                                }));
                              }}
                            >
                              <strong>{user.display_name}</strong>
                              <span className="userOptionMeta">
                                {user.membership_code} — {user.email}
                              </span>
                              {user.address && (
                                <span className="userOptionAddr">
                                  📍 {user.address}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label htmlFor="share-class">Class of Shares *</label>
                    <select
                      id="share-class"
                      value={issueForm.share_class}
                      onChange={(e) =>
                        setIssueForm((prev) => ({
                          ...prev,
                          share_class: e.target.value,
                        }))
                      }
                      className="formSelect"
                    >
                      <option value="Ordinary">Ordinary</option>
                      <option value="Preference">Preference</option>
                    </select>
                  </div>

                  <div className="formGroup">
                    <label htmlFor="num-shares">Number of Shares *</label>
                    <input
                      id="num-shares"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g. 100"
                      value={issueForm.number_of_shares}
                      onChange={(e) =>
                        setIssueForm((prev) => ({
                          ...prev,
                          number_of_shares: e.target.value,
                        }))
                      }
                      className="formInput"
                      required
                    />
                  </div>
                </div>

                <div className="formGroup">
                  <label htmlFor="amount-paid">Amount Paid (GH₵) *</label>
                  <input
                    id="amount-paid"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="e.g. 5000.00"
                    value={issueForm.amount_paid}
                    onChange={(e) =>
                      setIssueForm((prev) => ({
                        ...prev,
                        amount_paid: e.target.value,
                      }))
                    }
                    className="formInput"
                    required
                  />
                </div>

                <div className="formGroup">
                  <label htmlFor="cert-notes">Notes (Optional)</label>
                  <textarea
                    id="cert-notes"
                    placeholder="Any additional notes..."
                    value={issueForm.notes}
                    onChange={(e) =>
                      setIssueForm((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="formTextarea"
                    rows={3}
                  />
                </div>

                {issueError && <div className="formError">{issueError}</div>}
                {issueSuccess && (
                  <div className="formSuccess">{issueSuccess}</div>
                )}

                <div className="formActions">
                  <button
                    type="button"
                    className="cancelBtn"
                    onClick={() => setShowIssueModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submitBtn"
                    disabled={issueLoading || !selectedUser}
                  >
                    {issueLoading ? "Issuing..." : "Issue Certificate"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Revoke Modal */}
        {showRevokeModal && revokeTarget && (
          <div
            className="modalOverlay"
            onClick={() => setShowRevokeModal(false)}
          >
            <div
              className="modalContent revokeModal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modalHeader revoke">
                <h2>Revoke Certificate</h2>
                <button
                  className="closeBtn"
                  onClick={() => setShowRevokeModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="revokeInfo">
                <p>
                  You are about to revoke certificate{" "}
                  <strong>{revokeTarget.certificate_number}</strong> issued to{" "}
                  <strong>{revokeTarget.shareholder_name}</strong>.
                </p>
                <p className="revokeWarning">⚠ This action cannot be undone.</p>
              </div>

              <div className="formGroup">
                <label htmlFor="revoke-reason">Reason for Revocation *</label>
                <textarea
                  id="revoke-reason"
                  placeholder="Enter the reason for revoking this certificate..."
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  className="formTextarea"
                  rows={3}
                  required
                />
              </div>

              <div className="formActions">
                <button
                  className="cancelBtn"
                  onClick={() => setShowRevokeModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="revokeSubmitBtn"
                  onClick={handleRevoke}
                  disabled={revokeLoading || !revokeReason.trim()}
                >
                  {revokeLoading ? "Revoking..." : "Confirm Revoke"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificates;
